import { useEffect, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useApi } from '../lib/api.js';
import { useLang } from '../lib/i18n.js';
import { PageHead, EmptyState } from '../components/ui.js';
import { Icon, BrandGlyph } from '../components/Icon.js';

// Merchant sets the WhatsApp number that the AI deep-links customers to when
// escalating ("need a human" → wa.me/<phone>?text=...). This is the merchant's
// OWN WhatsApp Business number on their phone — ChatRunner never touches the
// WhatsApp API itself.

interface SupportConfig {
  support_whatsapp_phone: string | null;
  support_whatsapp_greeting_template: string;
}

export function SupportChannels() {
  const api = useApi();
  const qc = useQueryClient();
  const { t } = useLang();
  const { data, isLoading } = useQuery({
    queryKey: ['support-channels'],
    queryFn: () => api.get<SupportConfig>('/support-channels'),
  });

  const [phone, setPhone] = useState('');
  const [greeting, setGreeting] = useState('');
  const [dirty, setDirty] = useState(false);

  useEffect(() => {
    if (data) {
      setPhone(data.support_whatsapp_phone ?? '');
      setGreeting(data.support_whatsapp_greeting_template ?? '');
      setDirty(false);
    }
  }, [data]);

  const save = useMutation({
    mutationFn: () =>
      api.patch('/support-channels', {
        support_whatsapp_phone: phone.trim() || null,
        support_whatsapp_greeting_template: greeting,
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['support-channels'] });
      setDirty(false);
    },
  });

  const previewLink = buildPreview(phone, greeting);

  return (
    <div className="wrap section-gap">
      <PageHead
        title={t('page.whatsapp.title')}
        sub={t('page.whatsapp.sub')}
        actions={
          <button className="btn btn--primary" disabled={!dirty || save.isPending} onClick={() => save.mutate()}>
            <Icon name="check" />{save.isPending ? t('common.saving') : dirty ? t('common.save') : t('common.saved')}
          </button>
        }
      />

      {isLoading ? (
        <div className="muted" style={{ padding: 24 }}>{t('common.loading')}</div>
      ) : (
        <>
          <div className="card card--pad">
            <div className="field">
              <label className="field__label">{t('page.whatsapp.phone')}</label>
              <input
                className="input mono"
                dir="ltr"
                value={phone}
                onChange={(e) => { setPhone(e.target.value); setDirty(true); }}
                placeholder={t('page.whatsapp.phone.ph')}
              />
              <div className="field__hint">{t('page.whatsapp.phone.hint')}</div>
            </div>

            <div className="field" style={{ marginBottom: 0 }}>
              <label className="field__label">{t('page.whatsapp.msg')}</label>
              <textarea
                className="textarea"
                rows={3}
                value={greeting}
                onChange={(e) => { setGreeting(e.target.value); setDirty(true); }}
                placeholder={t('page.whatsapp.msg.ph')}
              />
              <div className="field__hint">{t('page.whatsapp.msg.hint')}</div>
            </div>
          </div>

          <div className="card card--pad">
            <div className="flex aic gap10" style={{ marginBottom: 14 }}>
              <BrandGlyph name="wa" size={20} />
              <div style={{ fontWeight: 800, fontSize: 14 }}>{t('page.whatsapp.preview')}</div>
            </div>
            {phone.trim() ? (
              <>
                <a
                  href={previewLink}
                  target="_blank"
                  rel="noreferrer"
                  className="btn"
                  style={{ background: '#25D366', color: '#053b22', fontWeight: 800 }}
                >
                  <BrandGlyph name="wa" size={14} />{t('page.whatsapp.test')}
                </a>
                <div
                  className="mono"
                  dir="ltr"
                  style={{
                    marginTop: 14, fontSize: 11.5, color: 'var(--ink-3)',
                    background: 'var(--bg-3)', border: '1px solid var(--line)',
                    borderRadius: 10, padding: '10px 12px', wordBreak: 'break-all',
                    whiteSpace: 'pre-wrap',
                  }}
                >{previewLink}</div>
              </>
            ) : (
              <EmptyState icon="phone" h={t('page.whatsapp.empty.h')} p={t('page.whatsapp.empty.p')} />
            )}
          </div>
        </>
      )}
    </div>
  );
}

function buildPreview(phone: string, greeting: string): string {
  const digits = phone.replace(/\D+/g, '');
  if (!digits) return '';
  const sample = `${greeting ? greeting + '\n\n' : ''}مرحباً، أنا [اسم العميل] وتواصلت معكم عبر إنستغرام.\nالسبب: مثال - استرجاع منتج\nالتفاصيل: مثال - الطلب رقم 123`;
  return `https://wa.me/${digits}?text=${encodeURIComponent(sample)}`;
}
